import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Grid,
    MenuItem,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import Controls from "../../components/controls/Controls";
import { tokens } from "../../assets/theme";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
    useWeekTemplates,
    useWeekTemplate,
    useUpdateWeekTemplate,
    useCreateWeekTemplate,
} from "../../hooks/useWeekSchedule";
import WeekTemplateEditor from "../../components/WeekTemplateEditor";
import AddIcon from "@mui/icons-material/Add";
import AxionControls from "../../components/controls/AxionControls";
import { pushErrorMessage, pushNotification } from "../../utils/notifications";
import messages from "./components/messages";
import { FormattedMessage, useIntl } from "react-intl";

function WeekScheduleIndex() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();

    const [companyKey] = useLocalStorage("companyKey", 1);

    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const { data: templates = [] } = useWeekTemplates(companyKey);
    const { data: template } = useWeekTemplate(
        typeof selectedTemplateId === "number" ? selectedTemplateId : null
    );

    const updateTemplateMutation = useUpdateWeekTemplate();
    const createTemplateMutation = useCreateWeekTemplate();

    const [localTemplate, setLocalTemplate] = useState(null);
    const [showBackdrop, setShowBackdrop] = useState(false);

    useEffect(() => {
        if (template) {
            setLocalTemplate(JSON.parse(JSON.stringify(template)));
        } else if (selectedTemplateId === "") {
            setLocalTemplate(null);
        }
    }, [template, selectedTemplateId]);

    const workspaceBg =
        theme.palette.mode === "dark" ? colors.primary[700] : colors.grey[100];

    const getRandomColor = () => {
        const palette = [
            "#26C6DA", // Cyan
            "#29B6F6", // Light Blue
            "#AB47BC", // Purple
            "#EF5350", // Red
            "#FFB300", // Amber
            "#66BB6A", // Green
            "#FF7043", // Deep Orange
            "#7E57C2", // Deep Purple 
            "#42A5F5", // Blue 
            "#26A69A", // Teal 
            "#9CCC65", // Light Green 
            "#EC407A", // Pink 
            "#5C6BC0", // Indigo
            "#8D6E63", // Brown
            "#D4E157", // Lime
            "#FFA726", // Orange
            "#78909C", // Blue Grey
        ];
        const idx = Math.floor(Math.random() * palette.length);
        return palette[idx];
    };

    const handleCreateNew = () => {
        setSelectedTemplateId("");
        setLocalTemplate({
            id: 0,
            name: "",
            isActive: true,
            companyKey: companyKey,
            templateColor: getRandomColor(), 
            days: [],
        });
    };

    const handleSave = async () => {
        if (!localTemplate) return;

        setShowBackdrop(true);

        try {
            if (!localTemplate.id || localTemplate.id === 0) {
                const payload = {
                    ...localTemplate,
                    companyKey: companyKey,
                };

                const created = await createTemplateMutation.mutateAsync(payload);

                if (created && created.id) {
                    setSelectedTemplateId(created.id);
                    setLocalTemplate(created);
                }

                pushNotification(formatMessage(messages.weekTemplateCreatedSuccessfully));
            } else {
                await updateTemplateMutation.mutateAsync({
                    id: localTemplate.id,
                    template: localTemplate,
                });

                pushNotification(formatMessage(messages.weekTemplateSavedSuccessfully));
            }
        } catch (error) {
            console.error(error);
            pushErrorMessage(formatMessage(messages.weekTemplateSaveFailed));
        } finally {
            setShowBackdrop(false);
        }
    };

    const isSaving =
        updateTemplateMutation.isPending || createTemplateMutation.isPending;

    return (
        <>
            <Box
                sx={{
                    p: 2,
                    backgroundColor: workspaceBg,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 1,
                    }}
                >
                    <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
                        <FormattedMessage {...messages.weekTemplates} />
                    </Typography>

                    <Controls.Button
                        text={formatMessage(messages.addWeekTemplate)}
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={handleCreateNew}
                        sx={{
                            "&:hover": {
                                backgroundColor: "#799c4a",
                            },
                            backgroundColor: "#799c4a",
                            color: "#ffffff",
                            textTransform: "none",
                            fontSize: "0.9rem",
                        }}
                    />
                </Box>

                {/* Välj schema + namn + färg */}
                <Grid container spacing={2} mb={1} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField
                            select
                            fullWidth
                            label=""
                            value={selectedTemplateId}
                            onChange={(e) =>
                                setSelectedTemplateId(
                                    e.target.value === "" ? "" : Number(e.target.value)
                                )
                            }
                            size="small"
                            SelectProps={{
                                displayEmpty: true,
                                renderValue: (value) => {
                                    if (value === "" || value == null) {
                                        return <em><FormattedMessage {...messages.selectWeekTemplate} /></em>;
                                    }
                                    const t = templates.find((x) => x.id === value);
                                    if (!t) return "";
                                    return (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 1,
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: 14,
                                                    height: 14,
                                                    borderRadius: "50%",
                                                    backgroundColor:
                                                        t.templateColor || "#888",
                                                    border:
                                                        "1px solid rgba(255,255,255,0.6)",
                                                }}
                                            />
                                            <span>{t.name}</span>
                                        </Box>
                                    );
                                },
                            }}
                        >
                            <MenuItem value="">
                                <em><FormattedMessage {...messages.selectWeekTemplate} /></em>
                            </MenuItem>
                            {templates.map((t) => (
                                <MenuItem key={t.id} value={t.id}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: 0.5,
                                                backgroundColor:
                                                    t.templateColor || "#888",
                                                border:
                                                    "1px solid rgba(255,255,255,0.6)",
                                            }}
                                        />
                                        <span>{t.name}</span>
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    {localTemplate && (
                        <>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    label={formatMessage(messages.nameOfWeekTemplate)}
                                    value={localTemplate.name || ""}
                                    onChange={(e) =>
                                        setLocalTemplate({
                                            ...localTemplate,
                                            name: e.target.value,
                                        })
                                    }
                                    size="small"
                                />
                            </Grid>

                            {/* Färg + color-picker */}
                            <Grid item xs={12} md={2}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ minWidth: 40 }}
                                    >
                                        <FormattedMessage {...messages.colorOfWeekTemplate} />
                                    </Typography>
                                    <Box
                                        component="input"
                                        type="color"
                                        value={localTemplate.templateColor || "#555555"}
                                        onChange={(e) =>
                                            setLocalTemplate({
                                                ...localTemplate,
                                                templateColor: e.target.value,
                                            })
                                        }
                                        sx={{
                                            width: 40,
                                            height: 32,
                                            p: 0,
                                            border: "1px solid rgba(255,255,255,0.4)",
                                            borderRadius: "4px",
                                            background: "transparent",
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </>
                    )}
                </Grid>

                {/* Skift-editor */}
                {localTemplate ? (
                    <>
                        <WeekTemplateEditor
                            template={localTemplate}
                            onTemplateChange={setLocalTemplate}
                        />

                        <Box mt={3}>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={isSaving || !localTemplate.name}
                                sx={{ 
                                    backgroundColor: colors.gray[500],
                                    "&:hover": { backgroundColor: colors.gray[700] },
                                    color: "#ffffff",
                                    textTransform: "none",
                                    fontSize: "0.9rem",
                                }}
                            >
                                {localTemplate.id && localTemplate.id !== 0
                                    ? formatMessage(messages.saveWeekTemplate)
                                    : formatMessage(messages.createWeekTemplate)}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        <FormattedMessage {...messages.selectWeekTemplateExplanation} />
                    </Typography>
                )}
            </Box>

            <AxionControls.AxionBackdrop open={showBackdrop} />
        </>
    );
}

export default WeekScheduleIndex;
